/**
 * @typedef BackInStockAttributes
 *
 * @property {boolean} [accepts_marketing]
 * @property {string} [phone_number] Required if email is null
 * @property {number} [quantity_required]
 */

/**
 * @typedef {Object} BackInStockSuccessResponse
 *
 * @property {'OK'} status
 * @property {string} message
 */

/**
 * @typedef {Object} BackInStockErrorResponse
 *
 * @property {'Error'} status
 * @property {Record<string, string[]>} errors
 */

class BackInStock {
  /**
   * @note It might be worth abstracting this further, for example simplifying the function signature
   * to have object containing email and phone number. That way this API could be easier to swap out
   * for other back ends in the future. It doesn't seem necessary at the moment though. - DW
   *
   * @param {string | null} email
   * @param {number} variantId
   * @param {number} productId
   * @param {BackInStockAttributes} [attributes]
   */
  static async subscribe (email, variantId, productId, attributes) {
    /** @type {BackInStockSuccessResponse | BackInStockErrorResponse} */
    const response = await window.BIS.create(
      email,
      variantId,
      productId,
      attributes,
    )

    if (response.status === 'Error') {
      response.message = response?.errors?.base?.length
        ? response?.errors?.base?.join(' ')
        : ''
    }

    return response
  }
}

class BackInStockForm extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'state', 'message']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'submit',
      this.handleFormSubmit.bind(this),
    )
  }

  /** @param {SubmitEvent} event */
  handleFormSubmit (event) {
    event.preventDefault()

    this.message = ''

    this.subscribe()
  }

  async subscribe () {
    const formData = new FormData(this.els.form.element)

    const response = await BackInStock.subscribe(
      formData.get('email'),
      this.variantId,
      this.productId,
      {
        accepts_marketing: !!formData.get('accepts_marketing'),
      },
    )

    this.state = response?.status?.toLowerCase() === 'ok' ? 'success' : 'idle'
    this.message = response?.message?.length ? response.message : ''

    this.log('subscribe', { response })
  }

  /** @returns {'idle' | 'success'} */
  get state () {
    const visibleState = this.els.view.elements.find(
      (view) => view.getAttribute('aria-hidden') !== 'true',
    )

    return visibleState.dataset.view
  }

  set state (state) {
    this.els.state.elements.forEach((element) => {
      element.setAttribute('aria-hidden', element.dataset.state !== state)
    })
  }

  get productId () {
    return Number(this.dataset.productId)
  }

  /** @todo unsure this updates as variant is changed */
  get variantId () {
    return Number(this.dataset.variantId)
  }

  get message () {
    return this._message
  }

  set message (message) {
    this._message = message

    this.els.message?.elements?.forEach((messageEl) => {
      messageEl.textContent = message
      messageEl.setAttribute('aria-hidden', !message?.length)
    })
  }
}

if (!window.customElements.get('back-in-stock-form')) {
  window.customElements.define('back-in-stock-form', BackInStockForm, {
    extends: 'aside',
  })
}
